import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import { Button, Card, Col, Container, Row, Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import secureLocalStorage from "react-secure-storage";
import Notification, { showToast } from "../../../components/common/notification/Notification";
import { useNavigate } from "react-router-dom";

// Validation schema
const validationSchema = Yup.object().shape({
  mode: Yup.string().required("Mode is required"),
  gateway: Yup.string().required("Gateway is required"),
  provider: Yup.string().required("Provider is required"),
  status: Yup.string().required("Status is required"),
});

const AfterPayment = () => {
  const navigate = useNavigate();
  const loggedInUser = useSelector((state) => state.auth.user);
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [showThankYou, setShowThankYou] = useState(false);

  const partner_id = loggedInUser.user.id;
  const paymentPartner = loggedInUser.user.payments[0];
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const initialValues = {
    mode: "",
    gateway: "",
    provider: "",
    status: "",
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: "image/*",
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = () =>
          setScreenshotFile({ file, preview: reader.result });
        reader.readAsDataURL(file);
      }
    },
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const formData = new FormData();
      formData.append("mode", values.mode);
      formData.append("gateway", values.gateway);
      formData.append("provider", values.provider);
      formData.append("status", values.status);
      formData.append("amount", localStorage.getItem("paymentAmount"));
      formData.append("user_id", partner_id);
      if (screenshotFile) formData.append("file", screenshotFile.file);

      const response = await axios.post(`${baseUrl}/all/payment/add`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.statusType === "SUCCESS") {
        showToast("Payment Details Uploaded Successfully", { type: "success" });
        secureLocalStorage.setItem("partnerP", "SUCCESS");
        setShowThankYou(true);
      }
    } catch (error) {
      showToast("Error Uploading Payment Details", { type: "danger" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="mt-5">
      <Notification/>

      <Row className="justify-content-center">
        {paymentPartner ? (
          <Col md={6}>
            <Card className="text-center p-5">
              <h4 className="text-success">Payment details uploaded successfully!</h4>
              <Button className="mt-4" onClick={() => console.log("submit clicked")}>
                Submit
              </Button>
            </Card>
          </Col>
        ) : (
          <Col md={8}>
            <Card >
              <Card.Header className="text-center text-white">
                <h3 className="text-primary">Share Your Payment Receipt</h3>
              </Card.Header>
              <Card.Body>
                <Formik
                  initialValues={initialValues}
                  onSubmit={handleSubmit}
                  validationSchema={validationSchema}
                >
                  {({ isSubmitting, isValid }) => (
                    <Form>
                      <Row className="g-3">
                        {/* Mode */}
                        <Col md={6}>
                          <div className="form-floating">
                            <Field
                              as="select"
                              className="form-select"
                              id="mode"
                              name="mode"
                            >
                              <option value="">Select option</option>
                              <option value="ONLINE">Online</option>
                            </Field>
                            <label htmlFor="mode">Mode</label>
                          </div>
                          <ErrorMessage name="mode" component="div" className="text-danger" />
                        </Col>

                        {/* Gateway */}
                        <Col md={6}>
                          <div className="form-floating">
                            <Field as="select" className="form-select" name="gateway" id="gateway">
                              <option value="">Select option</option>
                              <option value="UPI">UPI</option>
                              <option value="NETBANKING">Netbanking</option>
                            </Field>
                            <label htmlFor="gateway">Gateway</label>
                          </div>
                          <ErrorMessage name="gateway" component="div" className="text-danger" />
                        </Col>

                        {/* Provider */}
                        <Col md={6}>
                          <div className="form-floating">
                            <Field as="select" className="form-select" name="provider" id="provider">
                              <option value="">Select option</option>
                              <option value="PHONEPE">PhonePe</option>
                              <option value="GOOGLEPAY">Google Pay</option>
                              <option value="PAYTM">Paytm</option>
                            </Field>
                            <label htmlFor="provider">Provider</label>
                          </div>
                          <ErrorMessage name="provider" component="div" className="text-danger" />
                        </Col>

                        {/* Status */}
                        <Col md={6}>
                          <div className="form-floating">
                            <Field as="select" className="form-select" name="status" id="status">
                              <option value="">Select option</option>
                              <option value="SUCCESS">Success</option>
                              <option value="FAILURE">Failure</option>
                            </Field>
                            <label htmlFor="status">Status</label>
                          </div>
                          <ErrorMessage name="status" component="div" className="text-danger" />
                        </Col>

                        {/* Screenshot */}
                        <Col md={12}>
                          <label className="form-label fw-bold">Payment Screenshot</label>
                          <div
                            {...getRootProps()}
                            className={`border border-secondary rounded p-4 text-center mb-3 ${isDragActive ? "bg-light" : "bg-white"}`}
                            style={{ cursor: "pointer" }}
                          >
                            <input {...getInputProps()} />
                            <p className="mb-0">
                              {isDragActive ? "Drop the file here..." : "Drag & drop or click to select screenshot"}
                            </p>
                          </div>
                          {screenshotFile && (
                            <div className="text-center mb-3">
                              <h6>Preview</h6>
                              <img
                                src={screenshotFile.preview}
                                alt="Screenshot"
                                className="img-fluid rounded border"
                                style={{ maxHeight: "250px" }}
                              />
                            </div>
                          )}
                        </Col>

                        {/* Submit */}
                        <Col md={12} className="d-flex justify-content-end">
                          <Button type="submit" disabled={!isValid || isSubmitting} className="px-5">
                            {isSubmitting ? "Submitting..." : "Submit"}
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              </Card.Body>
            </Card>
          </Col>
        )}

        {/* Thank You Modal */}
        <Modal show={showThankYou} onHide={() => setShowThankYou(false)} centered>
          <Modal.Body className="text-center py-5">
            <div
              className="bg-success rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
              style={{ width: "80px", height: "80px" }}
            >
              <span className="text-white fs-1 fw-bold">✔</span>
            </div>
            <h4>Thank You!</h4>
            <p>Your payment details have been submitted successfully.</p>
            <Button
              variant="success"
              onClick={() => {
                setShowThankYou(false);
                navigate("/customer/workspace");
              }}
            >
              Close
            </Button>
          </Modal.Body>
        </Modal>
      </Row>
    </Container>
  );
};

export default AfterPayment;
